import { I18n } from "i18n-js";
import enAu from '@/locales/en-au/common.json';
import enUs from '@/locales/en-us/common.json';
import de from '@/locales/de/common.json';

export type Dictionary = Record<string, string>;

const translations: Record<string, Dictionary> = {
  'en-au': enAu,
  'en-us': enUs,
  'en': enAu, // Default to en-AU for generic 'en'
  de,
};

export const i18n = new I18n(translations);

i18n.defaultLocale = 'en-au';
i18n.enableFallback = true;

const LS_KEY = 'i18n_language';
const listeners: Array<(locale: string) => void> = [];

function normalizeLocale(input: string | undefined | null): string {
  if (!input) return 'en-au';
  const normalized = input.toLowerCase();
  if (translations[normalized]) return normalized;
  const short = normalized.split('-')[0];
  if (translations[short]) return short;
  return 'en-au';
}

export function detectLocale(): string {
  const fromStorage = localStorage.getItem(LS_KEY);
  if (fromStorage) return normalizeLocale(fromStorage);
  return normalizeLocale(navigator.language || (navigator as any).userLanguage);
}

export function initI18n(): void {
  const locale = detectLocale();
  i18n.locale = locale;
}

export function setLanguage(locale: string): void {
  const normalized = normalizeLocale(locale);
  i18n.locale = normalized;
  localStorage.setItem(LS_KEY, normalized);
  listeners.forEach((fn) => {
    try { fn(normalized); } catch (_) {}
  });
}

export function onLanguageChange(fn: (locale: string) => void): () => void {
  listeners.push(fn);
  return () => offLanguageChange(fn);
}

export function offLanguageChange(fn: (locale: string) => void): void {
  const idx = listeners.indexOf(fn);
  if (idx >= 0) listeners.splice(idx, 1);
}

export function getCurrentLocale(): string {
  return (i18n.locale as string) || 'en-au';
}

export function getCurrentDictionary(): Dictionary {
  const loc = normalizeLocale(getCurrentLocale());
  return translations[loc] || translations['en-au'];
}

export function t(key: string): string {
  try { return i18n.t(key) as string; } catch { return key; }
}

// Convenient namespace for debugging
if (typeof window !== 'undefined') {
  (window as any).__i18n_core__ = { i18n, setLanguage, getCurrentLocale, t };
}
